import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { z } from 'zod'

import { ApiError } from '@/api/client'
import { AuthLayout } from '@/components/AuthLayout'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { PasswordInput } from '@/components/ui/PasswordInput'
import { useAuth } from '@/features/auth/store'

const schema = z.object({
  email: z.string().min(1, 'Enter your email address').email('That is not a valid email address'),
  password: z.string().min(1, 'Enter your password'),
})

type FormValues = z.infer<typeof schema>

/**
 * The server's message is shown as-is for the codes below.
 *
 * `INVALID_CREDENTIALS` is deliberately vague on the server -- it answers identically for
 * an unknown email, a wrong password and a disabled account -- and this screen must not
 * undo that by guessing which one it was.
 */
const SHOW_SERVER_MESSAGE = new Set([
  'INVALID_CREDENTIALS',
  'ACCOUNT_LOCKED',
  'ACCOUNT_DISABLED',
  'RATE_LIMITED',
])

function messageFor(error: unknown): string {
  if (error instanceof ApiError) {
    if (SHOW_SERVER_MESSAGE.has(error.code)) return error.message
    if (error.status >= 500) return 'The server had a problem. Please try again in a moment.'
    return error.message
  }
  return 'Could not reach the server. Check that the API is running.'
}

export function Login() {
  const status = useAuth((s) => s.status)
  const signIn = useAuth((s) => s.signIn)
  const navigate = useNavigate()
  const location = useLocation()
  const [formError, setFormError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) })

  // Already signed in -- do not show a login form to someone who has a session.
  if (status === 'authenticated') {
    const from = (location.state as { from?: string } | null)?.from
    return <Navigate to={from ?? '/dashboard'} replace />
  }

  const onSubmit = handleSubmit(async ({ email, password }) => {
    setFormError(null)
    try {
      await signIn(email, password)
      const from = (location.state as { from?: string } | null)?.from
      navigate(from ?? '/dashboard', { replace: true })
    } catch (error) {
      setFormError(messageFor(error))
    }
  })

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Sign in to continue"
      footer={
        import.meta.env.DEV ? (
          // Dev builds only, and it has to be the account the seed actually creates: a
          // demo hint that does not sign in is worse than no hint at all.
          <p className="text-center text-xs leading-relaxed text-slate-500 dark:text-slate-400">
            <span className="font-medium text-slate-600 dark:text-slate-300">Local demo</span>
            <br />
            <code className="text-slate-500 dark:text-slate-400">
              sales.manager@demo.fmcg-erp.com
            </code>{' '}
            / <code className="text-slate-500 dark:text-slate-400">demo-password-1234</code>
          </p>
        ) : null
      }
    >
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        {formError && <Alert tone="error">{formError}</Alert>}

        <Field label="Email" error={errors.email?.message}>
          {(props) => (
            <Input
              {...props}
              {...register('email')}
              type="email"
              autoComplete="username"
              autoFocus
              placeholder="you@company.com"
            />
          )}
        </Field>

        <Field
          label="Password"
          error={errors.password?.message}
          // Beside the label rather than below the button: someone who cannot get in is
          // looking at the password box when they give up, not at the bottom of the form.
          action={
            <Link
              to="/forgot-password"
              className="text-xs font-medium text-brand-600 hover:underline dark:text-brand-400"
            >
              Forgot password?
            </Link>
          }
        >
          {(props) => (
            <PasswordInput
              {...props}
              {...register('password')}
              autoComplete="current-password"
              placeholder="••••••••"
            />
          )}
        </Field>

        <Button type="submit" className="w-full" loading={isSubmitting}>
          {isSubmitting ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </AuthLayout>
  )
}
